import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Pencil, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import Layout4 from "../DataLayouts/Layout4";
import {
  deleteApplicationUser,
  listApplicationUsers,
} from "../../Apis/ApplicationAdminUserApis";
import usePermissionDataStore from "../../Zustand/Store/usePermissionDataStore";
import {
  isWalletAdminUser,
  WALLET_ADMIN_ACTION,
} from "../../Utils/walletAdminAccess";
import { hasPermissionAction } from "../../Utils/organizationPermissions";
import useAuthStore from "../../Zustand/Store/useAuthStore";

export default function ApplicationUsersList() {
  const navigate = useNavigate();
  const { Permission } = usePermissionDataStore();
  const { userData } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);

  const walletAdmin = isWalletAdminUser(userData, Permission);
  const canAdd =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_ADD") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canEdit =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_EDIT") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);
  const canDelete =
    walletAdmin ||
    hasPermissionAction(Permission, "USER_DELETE") ||
    hasPermissionAction(Permission, WALLET_ADMIN_ACTION);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setUsers(await listApplicationUsers());
    } catch (err) {
      toast.error(err.message || "Failed to load users");
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (user) => {
    if (!canDelete) return;
    if (!window.confirm(`Delete user ${user.email}?`)) return;
    try {
      await deleteApplicationUser(user.application_user_id);
      toast.success("User deleted");
      load();
    } catch (err) {
      toast.error(err.message || "Delete failed");
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
      <Layout4
        heading="Users"
        subtitle="Application users and assigned roles"
        showAddButton={canAdd}
        btnName="Add user"
        Route="/users"
      />

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress size={32} />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Phone</TableCell>
                <TableCell>Role</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography color="text.secondary" py={2}>
                      No users yet.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                users.map((user) => (
                  <TableRow key={user.application_user_id} hover>
                    <TableCell>{user.full_name || "—"}</TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>
                      {[user.country_phone_code, user.phone_number]
                        .filter(Boolean)
                        .join(" ") || "—"}
                    </TableCell>
                    <TableCell>{user.role_name || "—"}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={user.is_active ? "Active" : "Inactive"}
                        color={user.is_active ? "success" : "default"}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                      >
                        {canEdit && (
                          <IconButton
                            size="small"
                            onClick={() =>
                              navigate(
                                `/users/edit/${user.application_user_id}`
                              )
                            }
                          >
                            <Pencil size={16} />
                          </IconButton>
                        )}
                        {canDelete && (
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(user)}
                          >
                            <Trash2 size={16} />
                          </IconButton>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
